import { formatDistanceToNow, format, isValid, parseISO } from "date-fns";
import { ar, zhCN, enUS } from "date-fns/locale";
import type { Locale } from "date-fns";
import type { SupportedLocale, TranslationKeys } from "@chinese-vendor/i18n";

// ─── Types ────────────────────────────────────────────────────────────────────

// Kept for call-site compatibility — date-fns handles its own localisation.
export type TranslateFunction = (
  key: TranslationKeys,
  interpolations?: Record<string, string | number>
) => string;

// ─── Locale map ───────────────────────────────────────────────────────────────

const DATE_FNS_LOCALE: Record<SupportedLocale, Locale> = {
  en: enUS,
  "zh-CN": zhCN,
  ar,
};

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Parses an ISO string (or any date string) into a valid Date.
 * Returns null for null / undefined / empty / unparseable input.
 */
function parseDate(isoString: string | null | undefined): Date | null {
  if (!isoString) return null;
  const d = parseISO(isoString);
  if (isValid(d)) return d;
  // Fallback for non-ISO strings (e.g. "Mar 15, 2024")
  const fallback = new Date(isoString);
  return isValid(fallback) ? fallback : null;
}

// ─── Public helpers ───────────────────────────────────────────────────────────

/**
 * Returns a locale-aware relative time string (e.g. "3 minutes ago").
 * Uses date-fns formatDistanceToNow with locale support.
 * The `t` parameter is kept for call-site compatibility but is no longer used.
 */
export function formatRelativeTime(
  isoString: string | null | undefined,
  locale: SupportedLocale,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _t?: TranslateFunction
): string {
  const d = parseDate(isoString);
  if (!d) return "—";
  return formatDistanceToNow(d, {
    addSuffix: true,
    locale: DATE_FNS_LOCALE[locale],
  });
}

/**
 * Returns a locale-aware absolute date/time string
 * (e.g. "Mar 16, 2026, 02:30 PM").
 */
export function formatAbsoluteDate(
  isoString: string | null | undefined,
  locale: SupportedLocale
): string {
  const d = parseDate(isoString);
  if (!d) return "—";
  return format(d, "MMM dd, yyyy, hh:mm aa", { locale: DATE_FNS_LOCALE[locale] });
}

/**
 * Returns date and time as separate formatted strings, suitable for
 * rendering the two parts independently with a divider between them
 * (e.g. activity log timestamps).
 *
 * Date format: dd/MM/yyyy  →  "15/03/2026"
 * Time format: hh:mm aa    →  "02:28 PM"
 */
export function formatSplitDateTime(
  isoString: string | null | undefined,
  locale: SupportedLocale
): { date: string; time: string } {
  const d = parseDate(isoString);
  if (!d) return { date: "—", time: "—" };
  return {
    date: format(d, "dd/MM/yyyy", { locale: DATE_FNS_LOCALE[locale] }),
    time: format(d, "hh:mm aa", { locale: DATE_FNS_LOCALE[locale] }),
  };
}
